import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import makeAnimated from "react-select/animated";
import { MagnifyingGlassIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  generateV2ServiceCategoryOptions,
  isNewServiceCategory,
  getAncestors,
  getDescendants,
} from "../utils/serviceCategoryV2Mapping";
import Datepicker from "react-tailwindcss-datepicker";
import { UseGetRfiListForFilters } from "../custom-hooks/queries";
import { format } from "date-fns"; // Import date-fns for formatting
import ComboBox from "../components/ComboBox";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import LocationDrawer from "../components/locationDrawer";
import ProviderContactDrawer from "../components/ProviderContactDrawer";
import LocationFields from "../components/LocationFields";
import ContactFields from "../components/ContactFields";
import NotificationModal from "../components/NotificationModal";
import MultiSelectWithController from "../components/MultiSelectWithController";
import { fortyFiveDays } from "../utils/helperMethods";
import { getGoogleMapsApiKey } from "../config/googleMapsConfig";
import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  countryOptions,
  getFilteredCountryOptions,
  getFilteredStateOptions,
  getStateOptions,
  parseAddressComponents,
} from "../utils/locationData";
import { onBlurSelectWithDelay } from "../utils/dom-events";
import { getLocationFromProvider } from "../utils/addressUtils";
import CustomTooltip from "../components/Filters/CustomTooltip";

const animatedComponents = makeAnimated();
const newContactObj = {
  id: "new-contact",
  firstName: "",
  lastName: "",
  email: "",
  fullContact: "",
  primary: false,
};

export const ExistingProvider = ({
  formData,
  setFormData,
  otherAddress,
  setOtherAddress,
  providerDetails,
  locations,
  providerLocations, // Add this prop to access the full provider data
  currentSelectedAssessmentContact,
  currentSetSelectedAssessmentContact,
  currentSelectedAssessmentLocation,
  currentSetSelectedAssessmentLocation,
  additionalContacts,
  setAdditionalContacts,
  onChangeNewLocation,
  isPending
}) => {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  // Additional effect to ensure we catch all location changes
  React.useEffect(() => {
    if (locations && locations.length > 0) {
      const primary =
        locations.find((loc) => loc.location_type === "primary") ||
        locations.find(
          (loc) =>
            loc.location_type === null || loc.location_type === undefined,
        ) ||
        locations[0]; // Fallback to first location
      const secondary = locations.find(
        (loc) => loc.location_type === "secondary",
      );

      if (primary?.address1) {
        // For primary locations, append provider's city and country to address1
        let addressLabel = primary.address1;
        if (primary.location_type === "primary") {
          const cityCountry = [];
          if (providerLocations?.city) cityCountry.push(providerLocations.city);
          if (providerLocations?.country)
            cityCountry.push(providerLocations.country);

          if (cityCountry.length > 0) {
            addressLabel = `${primary.address1}, ${cityCountry.join(", ")}`;
          }
        }
        setValue("primaryLocationTextBox", addressLabel);
      }
      if (secondary?.address1) {
        setValue("secondaryLocationTextBox", secondary.address1);
      } else if (providerLocations?.provider_locations?.[0]?.address2) {
        // Check for address2 in provider_locations when no secondary location with location_type exists
        setValue(
          "secondaryLocationTextBox",
          providerLocations.provider_locations[0].address2,
        );
      }
    }
  }, [locations, setValue, providerLocations]); // Add providerLocations dependency

  // Set locationType to "other" when additional location is populated
  useEffect(() => {
    if (otherAddress && otherAddress.trim().length > 0) {
      setValue("locationType", "other");
    }
  }, [otherAddress, setValue]);

  const { data: rfis } = UseGetRfiListForFilters();
  const optionsForMultiSelect = rfis?.map((item) => ({
    value: item?.rfi_id,
    label: item?.rfi_name,
  }));
  // ── V2 Service Categories dropdown state ──
  const [isSCDropdownOpen, setIsSCDropdownOpen] = useState(false);
  const [scSearchQuery, setSCSearchQuery] = useState("");
  const scDropdownRef = useRef(null);
  const scSearchInputRef = useRef(null);

  const v2SCOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const filteredSCOptions = useMemo(() => {
    if (!v2SCOptions) return [];
    if (!scSearchQuery.trim()) return v2SCOptions;
    const q = scSearchQuery.toLowerCase();
    const matchingGroups = new Set();
    v2SCOptions.forEach((opt) => {
      if (!opt.isGroupHeader && opt.label.toLowerCase().includes(q)) {
        matchingGroups.add(opt.groupName);
      }
    });
    return v2SCOptions.filter((opt) => {
      if (opt.isGroupHeader) return matchingGroups.has(opt.label);
      return opt.label.toLowerCase().includes(q);
    });
  }, [v2SCOptions, scSearchQuery]);

  // Name→ID lookup for V2 options
  const scNameToId = useMemo(() => {
    const map = {};
    (v2SCOptions || []).forEach((o) => {
      if (!o.isGroupHeader && o.rfiId) map[o.value] = o.rfiId;
    });
    return map;
  }, [v2SCOptions]);

  // Helper: create { value: rfi_id, label: rfi_name } matching original shape
  const makeSCEntry = (name) => ({
    value: scNameToId[name] || name,
    label: name,
  });

  const handleSCToggle = (value) => {
    const opt = v2SCOptions?.find((o) => o.value === value);
    if (!opt) return;
    // currentIds = array of rfi_id values currently selected
    const currentIds = (watch("serviceCategories") || []).map((t) => t.value || t);

    if (opt.isGroupHeader) {
      const members = opt.groupMembers || [];
      const memberIds = members.map((m) => scNameToId[m]).filter(Boolean);
      const allSelected = memberIds.every((id) => currentIds.includes(id));
      if (allSelected) {
        const idsToRemove = new Set(memberIds);
        const remaining = (watch("serviceCategories") || []).filter((t) => !idsToRemove.has(t.value));
        setValue("serviceCategories", remaining);
      } else {
        const existing = new Set(currentIds);
        const toAdd = members.filter((m) => scNameToId[m] && !existing.has(scNameToId[m])).map((m) => makeSCEntry(m));
        setValue("serviceCategories", [...(watch("serviceCategories") || []), ...toAdd]);
      }
      return;
    }

    const optId = scNameToId[value];
    const isSelected = currentIds.includes(optId);
    if (isSelected) {
      setValue("serviceCategories", (watch("serviceCategories") || []).filter((t) => t.value !== optId));
    } else {
      const namesToAdd = [value, ...getAncestors(value), ...getDescendants(value)];
      const existing = new Set(currentIds);
      const toAdd = namesToAdd
        .filter((n) => scNameToId[n] && !existing.has(scNameToId[n]) && v2SCOptions.some((o) => o.value === n))
        .map((n) => makeSCEntry(n));
      setValue("serviceCategories", [...(watch("serviceCategories") || []), ...toAdd]);
    }
  };

  const removeSCTag = (rfiId) => {
    setValue("serviceCategories", (watch("serviceCategories") || []).filter((t) => t.value !== rfiId));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (scDropdownRef.current && !scDropdownRef.current.contains(e.target)) {
        setIsSCDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // ── End V2 Service Categories ──

  const [address, setAddress] = useState("");
  const [addressPrimary, setAddressPrimary] = useState("");
  const [autocomplete, setAutocomplete] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [drawerLocationOpen, setDrawerLocationOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [localContacts, setLocalContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);

  const [primaryAddress, setPrimaryAddress] = useState("");
  const [primaryCity, setPrimaryCity] = useState("");
  const [primaryCountry, setPrimaryCountry] = useState("");
  const [primaryState, setPrimaryState] = useState("");
  const [primaryCountrySearch, setPrimaryCountrySearch] = useState("");
  const [primaryStateSearch, setPrimaryStateSearch] = useState("");
  const [
    isAssessmentLocationDropdownOpen,
    setIsAssessmentLocationDropdownOpen,
  ] = useState(false);
  const [isPrimaryCountryDropdownOpen, setIsPrimaryCountryDropdownOpen] =
    useState(false);
  const [isPrimaryStateDropdownOpen, setIsPrimaryStateDropdownOpen] =
    useState(false);

  const getContactsForProvider = (provider) => {
    if (!provider) return [];

    // Get provider users if available
    if (provider.provider_user?.length > 0) {
      return provider.provider_user;
    }

    // Otherwise use contact_email as fallback (if available)
    if (provider.contact_email) {
      const fallbackContact = {
        name: provider.contact_name || "",
        email: provider.contact_email,
      };
      return [fallbackContact];
    }

    return [];
  };

  const apiContacts = getContactsForProvider(providerDetails);
  const allContacts = [...apiContacts, ...additionalContacts];

  useEffect(() => {
    if (allContacts.length > 0) {
      if (allContacts[0]?.email !== currentSelectedAssessmentContact?.email) {
        currentSetSelectedAssessmentContact(allContacts[0]);
        setAdditionalContacts([]);
      }
    }
    if (allContacts.length === 0) {
      currentSetSelectedAssessmentContact(newContactObj);
      setAdditionalContacts([newContactObj]);
    }
  }, [providerDetails, currentSetSelectedAssessmentContact]);

  const handleOnLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  // Called when the user selects an address from the dropdown
  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      const { streetAddress, city, state, countryCode } =
        parseAddressComponents(place);

      setPrimaryAddress(streetAddress);
      setPrimaryCity(city);
      setPrimaryState(state);
      setPrimaryCountry(countryCode);
      onChangeNewLocation({
        address: streetAddress,
        city: city,
        state: state,
        country: countryCode,
      });

      setValue("primaryAddress", streetAddress);
      setValue("primaryCity", city);
      setValue("primaryState", state);
      setValue("primaryCountry", countryCode);
      setIsPrimaryCountryDropdownOpen(false);
      setIsPrimaryStateDropdownOpen(false);
    }
  };
  const onsiteRemote = [
    { label: "Remote", value: false },
    { label: "Onsite", value: false },
  ];
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
      color: state.isFocused ? "white" : "black", // White text on hover for better contrast
    }),
  };
  const hasAdditionalAddress = otherAddress && otherAddress.trim().length > 0;
  const locationType =
    watch("locationType") || (hasAdditionalAddress ? "other" : "primary"); // Watch the locationType field to determine which is selected

  const handleAddContact = (newContact) => {
    setLocalContacts((prev) => [...prev, newContact]);
    setValue("providerContactDetails", newContact);
    setContactDrawerOpen(false);
  };

  const [isAssessmentContactDropdownOpen, setIsAssessmentContactDropdownOpen] =
    useState(false);

  const availableContacts = [
    ...(allContacts
      .map((contact, index) => ({
        id: contact.email || `contact-${index}`, // Add unique id for each contact
        ...contact, // Keep all original properties
        firstName: contact.first_name || "",
        lastName: contact.last_name || "",
        name: contact.name || "",
        email: contact.email || "",
        fullContact: [
          [contact.first_name, contact.last_name, contact.title].join(" "),
          // contact.email,
        ].join(", "),
        label:
          contact.primary ||
          (index === 0 && contact.email === apiContacts[0]?.email)
            ? "Preferred Contact"
            : `Additional Contact ${index}`,
        // Set primary flag for the first provider_user contact
        primary: index === 0 && contact.email === apiContacts[0]?.email,
      }))
      .filter((loc) => loc.firstName) || []),
    newContactObj,
  ];

  const addAdditionalContact = () => {
    setAdditionalContacts((prev) => [
      ...prev,
      {
        firstName: "",
        lastName: "",
        title: "",
        email: "",
      },
    ]);
  };

  const updateAdditionalContact = (index, field, value) => {
    setAdditionalContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact,
      ),
    );
  };

  const removeAdditionalContact = (index) => {
    setAdditionalContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const existingLocations = {};
  const availableLocations = useMemo(
    () => [
      ...(locations
        ?.map((loc) => loc.provider_locations)
        ?.flat()
        ?.filter((loc) => loc.address || loc.address1)
        ?.sort((a, b) => a.location_type === "primary" ? -1 : a.created_at > b.created_at ? 1 : -1)
        ?.map((loc, i) => ({
          ...loc,
          id: Date.now() + i,
          address: loc.address || loc.address1,
          label:
            loc.location_type === "primary"
              ? "Primary Location"
              : `Additional Location ${i}`,
          fullAddress: getLocationFromProvider(loc, { short: false, abbvState: true }),
        }))
        .filter((loc) => {
          const exist = !existingLocations[loc.fullAddress];
          existingLocations[loc.fullAddress] = true;
          return exist;
        }) || []),
      {
        id: "new-location",
        label: "",
        fullAddress: "",
      },
    ],
    [locations],
  );

  useEffect(() => {
    if (providerDetails && availableLocations.length > 0) {
      currentSetSelectedAssessmentLocation(availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0]);
    }
  }, [providerDetails, availableLocations]);

  useEffect(() => {
    if (
      availableContacts.length > 0 &&
      !currentSelectedAssessmentContact &&
      availableContacts[0].id !== "new-contact"
    ) {
      currentSetSelectedAssessmentContact(availableContacts[0]);
    }
    if (
      currentSelectedAssessmentContact &&
      !availableContacts.find(
        (loc) => loc.id === currentSelectedAssessmentContact.id,
      )
    ) {
      currentSetSelectedAssessmentContact(
        availableContacts.length > 0 ? availableContacts[0] : null,
      );
    }
  }, [
    availableContacts,
    currentSelectedAssessmentContact,
    currentSetSelectedAssessmentContact,
  ]);

  return (
    <div className="px-5">
      <div className="flex flex-col border-b border-gray-300 py-5 lg:py-6">
        <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6">
          <div className="flex items-center justify-between">
            <h1 className="block text-sm font-medium text-gray-900 md:mt-0">
              Contact
            </h1>
          </div>
          <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
            {availableContacts.length > 0 && availableContacts[0].id !== "" ? (
              <>
                {
                  // Multiple contacts - show custom dropdown with bold labels
                  <div className="sm:max-w-xl relative assessment-location-dropdown">
                    <div
                      className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.contact ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.contact ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setIsAssessmentContactDropdownOpen(
                            !isAssessmentContactDropdownOpen,
                          )
                        }
                        onBlur={(e) =>
                          onBlurSelectWithDelay(
                            e,
                            setIsAssessmentContactDropdownOpen,
                          )
                        } // Use the new onBlurSelect function
                        className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                      >
                        <span className="block truncate text-gray-900">
                          {currentSelectedAssessmentContact ? (
                            (() => {
                              const selected = availableContacts.find(
                                (loc) =>
                                  loc.id ===
                                  currentSelectedAssessmentContact?.id,
                              );
                              return selected ? (
                                selected?.id === "new-contact" ? (
                                  <span className="italic text-gray-500">
                                    Add a contact that is not listed above
                                  </span>
                                ) : (
                                  // Regular contact display
                                  <>
                                    <span className="font-medium">
                                      {selected.label}:
                                    </span>{" "}
                                    {selected.fullContact}
                                  </>
                                )
                              ) : (
                                "Select assessment contact"
                              );
                            })()
                          ) : (
                            <span className="text-gray-500">
                              Select assessment contact
                            </span>
                          )}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </div>

                    {isAssessmentContactDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {availableContacts?.map((contact) => (
                          <div
                            key={contact.id}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                              contact.isNewLocation
                                ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                : ""
                            } ${
                              currentSelectedAssessmentContact?.id ===
                              contact.id
                                ? "text-indigo-600 bg-indigo-50"
                                : "text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              currentSetSelectedAssessmentContact(contact);
                              if (contact.id === "new-contact") {
                                setAdditionalContacts([contact]);
                              }
                              setIsAssessmentContactDropdownOpen(false);
                            }}
                          >
                            <span className="block truncate">
                              {contact?.id === "new-contact" ? (
                                // Special styling for "New Location"
                                <span className="italic text-gray-500">
                                  Add a contact that is not listed above
                                </span>
                              ) : (
                                // Regular location display
                                <>
                                  <span className="font-medium">
                                    {contact.label}:
                                  </span>{" "}
                                  {contact.fullContact}
                                </>
                              )}
                            </span>
                            {currentSelectedAssessmentContact?.id ===
                              contact.id && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                <CheckIcon
                                  className="h-5 w-5 text-indigo-600"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                }
              </>
            ) : (
              // No contacts available - show message like preferred contact
              <div className="sm:max-w-xl text-sm sm:pt-1.5">
                <span className="text-gray-500 italic">
                  Please first select or add a vendor in the section above.
                </span>
              </div>
            )}
            {errors.contact && (
              <p className="mt-2 text-sm text-red-600">
                {errors.contact.message}
              </p>
            )}
          </div>
        </div>

        {(availableContacts?.length === 0 ||
          currentSelectedAssessmentContact?.id === "new-contact") &&
          additionalContacts?.map((contact, index) => {
            const contactLabel =
              index === 0
                ? "Additional Contact"
                : `Additional Contact ${index + 1}`;
            return (
              <div
                key={index}
                className={`sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 ${index === 0 ? "sm:border-t sm:border-gray-200" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    {contactLabel}
                  </label>
                  {availableContacts?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        removeAdditionalContact(index);
                        currentSetSelectedAssessmentContact(null);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove contact"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                  <ContactFields
                    errors={{}}
                    fieldPrefix={`additional_${index}_`}
                    firstName={contact.firstName}
                    setFirstName={(v) =>
                      updateAdditionalContact(index, "firstName", v)
                    }
                    lastName={contact.lastName}
                    setLastName={(v) =>
                      updateAdditionalContact(index, "lastName", v)
                    }
                    title={contact.title}
                    setTitle={(v) =>
                      updateAdditionalContact(index, "title", v)
                    }
                    email={contact.email}
                    setEmail={(v) =>
                      updateAdditionalContact(index, "email", v)
                    }
                    fullWidth
                  />
                </div>
              </div>
            );
          })}

        {/* Add Additional Contact Button - Left margin */}
        {/* <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-6 sm:pb-2">
          <div className="sm:col-span-1">
            <button
              type="button"
              onClick={addAdditionalContact}
              className="text-indigo-600 text-sm cursor-pointer hover:underline hover:text-indigo-700 focus:outline-none"
            >
              Add Additional Contact
            </button>
          </div>
          <div className="sm:col-span-2"></div>
        </div> */}
      </div>
      {/* Assessment Location */}
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
        <h1 className="font-semibold">Request Information</h1>
        <p className="text-sm text-gray-600">
          Specify the location, service category, desired draft date information
          below.
        </p>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 lg:py-6">
        <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
          Location
        </h1>

        <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
          {availableLocations.length > 0 &&
          availableLocations[0].id !== "new-location" ? (
            <>
              <div className="sm:max-w-xl relative assessment-location-dropdown">
                <div
                  className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.location ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.location ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setIsAssessmentLocationDropdownOpen(
                        !isAssessmentLocationDropdownOpen,
                      )
                    }
                    onBlur={(e) =>
                      onBlurSelectWithDelay(
                        e,
                        setIsAssessmentLocationDropdownOpen,
                      )
                    } // Use the new onBlurSelect function
                    className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                  >
                      <span className="flex items-center truncate text-gray-900">
                      {currentSelectedAssessmentLocation ? (
                        (() => {
                          const selected = availableLocations.find(
                            (loc) =>
                              loc.id === currentSelectedAssessmentLocation?.id,
                          );
                          return selected ? (
                            selected.id === "new-location" ? (
                              // Special styling for "New Location" option
                              <span className="italic text-gray-500">
                                Request a location that is not listed above
                              </span>
                            ) : (
                              // Regular location display
                                <>
                                  <span className="font-medium">
                                    {selected.label}:
                                  </span>{" "}
                                  <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={selected.fullAddress}>
                                    {selected.fullAddress}
                                  </CustomTooltip>
                                </>
                            )
                          ) : (
                            "Select assessment location"
                          );
                        })()
                      ) : (
                        <span className="text-gray-500">
                          Select assessment location
                        </span>
                      )}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </div>

                {isAssessmentLocationDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {availableLocations?.map((location) => (
                      <div
                        key={location.id}
                        className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                          location.isNewLocation
                            ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                            : ""
                        } ${
                          currentSelectedAssessmentLocation?.id === location.id
                            ? "text-indigo-600 bg-indigo-50"
                            : "text-gray-900 hover:bg-gray-50"
                        }`}
                        onClick={() => {
                          currentSetSelectedAssessmentLocation(location);
                          setIsAssessmentLocationDropdownOpen(false);
                        }}
                      >
                        <span className="flex items-center truncate">
                          {location.id === "new-location" ? (
                            // Special styling for "New Location"
                            <span className="italic text-gray-500">
                              Request a location that is not listed above
                            </span>
                          ) : (
                            // Regular location display
                              <>
                                <span className="font-medium">
                                  {location.label}: 
                                </span>{" "}
                                <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={location.fullAddress}>
                                  {location.fullAddress}
                                </CustomTooltip>
                              </>
                          )}
                        </span>
                        {currentSelectedAssessmentLocation?.id ===
                          location.id && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                            <CheckIcon
                              className="h-5 w-5 text-indigo-600"
                              aria-hidden="true"
                            />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            // No locations available - show message like preferred contact
            <div className="sm:max-w-xl text-sm sm:pt-1.5">
              <span className="text-gray-500 italic">
                Please first select or add a vendor in the section above.
              </span>
            </div>
          )}
          {errors.location && (
            <p className="mt-2 text-sm text-red-600">
              {errors.location.message}
            </p>
          )}
        </div>
      </div>
      {currentSelectedAssessmentLocation?.id === "new-location" && (
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 lg:py-3">
          <div className="flex items-center justify-between sm:pt-1.5">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Add new Location
            </h1>
            {availableLocations?.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  currentSetSelectedAssessmentLocation(
                    availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0] || null,
                  );
                  setPrimaryAddress("");
                  setPrimaryCity("");
                  setPrimaryState("");
                  setPrimaryCountry("");
                  onChangeNewLocation({
                    address: "",
                    city: "",
                    state: "",
                    country: "",
                  });
                }}
                className="text-gray-400 hover:text-red-500 transition-colors"
                aria-label="Remove location"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
            <LocationFields
              control={control}
              errors={errors}
              handleOnLoad={handleOnLoad}
              handlePlaceChanged={handlePlaceChanged}
              address={primaryAddress}
              setAddress={setPrimaryAddress}
              setValue={setValue}
              onChangeLocation={onChangeNewLocation}
              city={primaryCity}
              setCity={setPrimaryCity}
              state={primaryState}
              setState={setPrimaryState}
              country={primaryCountry}
              setCountry={setPrimaryCountry}
              countrySearch={primaryCountrySearch}
              setCountrySearch={setPrimaryCountrySearch}
              isCountryDropdownOpen={isPrimaryCountryDropdownOpen}
              setIsCountryDropdownOpen={setIsPrimaryCountryDropdownOpen}
              isStateDropdownOpen={isPrimaryStateDropdownOpen}
              setIsStateDropdownOpen={setIsPrimaryStateDropdownOpen}
              stateSearch={primaryStateSearch}
              setStateSearch={setPrimaryStateSearch}
              classes="w-[330px] lg:w-[400px] xl:w-[500px]"
            />
          </div>
        </div>
      )}
      {/* Service Categories */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 md:mt-0">
        <label
          htmlFor="service-categories"
          className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
        >
          Service Categories*
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          {/* V2 grouped dropdown */}
          {
            <div className="relative w-[330px] lg:w-[400px] xl:w-[500px]" ref={scDropdownRef}>
              <div
                className="flex flex-wrap items-center gap-1.5 min-h-[38px] rounded-md border-0 bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-inset ring-gray-200 cursor-pointer hover:bg-gray-50 transition-colors focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600"
                onClick={() => setIsSCDropdownOpen(!isSCDropdownOpen)}
              >
                {(watch("serviceCategories") || []).length > 0 ? (
                  (watch("serviceCategories") || []).map((tag) => {
                    const tagValue = tag.value || tag;
                    const tagLabel = tag.label || tag;
                    const rfiItem = rfis?.find((r) => r.rfi_id === tagValue || r.rfi_name === tagValue);
                    const bgColor = rfiItem?.rfi_color_code_bg || "#EEF2FF";
                    const textColor = rfiItem?.rfi_color_code_text || "#4338CA";
                    const borderColor = rfiItem?.rfi_color_code_border || "#4338CA";
                    return (
                      <span
                        key={tagValue}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                          borderColor: borderColor,
                          '--tw-ring-color': borderColor + '33',
                        }}
                      >
                        {tagLabel}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeSCTag(tagValue); }}
                          className="ml-0.5 inline-flex items-center hover:opacity-70"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-sm text-gray-500">Select service categories</span>
                )}
                <span className="pointer-events-none ml-auto flex items-center">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </div>

              {isSCDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
                    <div className="relative">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        ref={scSearchInputRef}
                        type="text"
                        className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:outline-none"
                        placeholder="Search categories..."
                        value={scSearchQuery}
                        onChange={(e) => setSCSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-[26rem] overflow-y-auto py-1 px-3" style={{ paddingBottom: "1rem" }}>
                    <div className="space-y-2.5">
                      {filteredSCOptions.map((option, idx) => {
                        if (option.isGroupHeader) {
                          const members = option.groupMembers || [];
                          const currentIds = (watch("serviceCategories") || []).map((t) => t.value || t);
                          const selectedInGroup = members.filter((m) => currentIds.includes(scNameToId[m]));
                          const allSelected = members.length > 0 && selectedInGroup.length === members.length;
                          const someSelected = selectedInGroup.length > 0 && !allSelected;
                          return (
                            <div key={`grp-${idx}`} className="pt-3 pb-1 first:pt-0">
                              <span
                                className={`text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                                onClick={(e) => { e.stopPropagation(); handleSCToggle(option.value); }}
                              >
                                {option.label}
                              </span>
                            </div>
                          );
                        }
                        const currentTags = (watch("serviceCategories") || []).map((t) => t.value || t);
                        const currentIds = (watch("serviceCategories") || []).map((t) => t.value || t);
                        const isSelected = currentIds.includes(option.rfiId);
                        const indentPx = (option.depth || 0) * 16;
                        return (
                          <div key={`sc-${idx}`} className="flex items-start gap-3" style={{ paddingLeft: `${indentPx}px` }}>
                            <div className="group grid size-4 grid-cols-1 mt-0.5 flex-shrink-0">
                              <input
                                value={option.value}
                                checked={isSelected}
                                onChange={(e) => { e.stopPropagation(); handleSCToggle(option.value); }}
                                id={`sc-filter-${idx}`}
                                type="checkbox"
                                className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                              />
                              <svg viewBox="0 0 16 16" fill="white" className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100">
                                <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                              </svg>
                            </div>
                            <label htmlFor={`sc-filter-${idx}`} className="text-sm font-medium text-gray-900 leading-relaxed cursor-pointer flex-1">
                              {option.label}
                            </label>
                          </div>
                        );
                      })}
                      {filteredSCOptions.length === 0 && (
                        <div className="py-3 text-center text-sm text-gray-500">No matching categories</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          }

          {errors.serviceCategories && (
            <p className="text-red-500 text-sm">
              {errors.serviceCategories.message}
            </p>
          )}
        </div>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
        <h1 className="block text-sm font-medium text-gray-900">
          Onsite / Remote*
        </h1>
        <div className="md:w-[390px]">
          {/* Controller for Onsite/Remote Dropdown */}
          <Controller
            name="onsiteRemote" // Matches the Yup schema
            control={control}
            defaultValue={{ onsite: false, remote: true }} // Default value is Remote
            render={({ field }) => (
              <ComboBox
                data={[
                  { value: false, name: "Remote" },
                  { value: true, name: "Onsite" },
                ]}
                placeHolder="Select..."
                setSelected={(value) => {
                  // Add a null check before trying to access value.name
                  if (value) {
                    // Update the value of onsiteRemote dynamically
                    setValue("onsiteRemote", {
                      onsite: value.name === "Onsite",
                      remote: value.name === "Remote",
                    });
                  } else {
                    // Handle the case when value is undefined - set default values
                    setValue("onsiteRemote", {
                      onsite: false,
                      remote: true, // Default to remote
                    });
                  }
                }}
                selected={
                  field.value.onsite
                    ? { value: true, name: "Onsite" }
                    : { value: false, name: "Remote" }
                }
                width="w-[500px]"
                inputWidth="w-[500px]"
                showContactFormatting={true}
              />
            )}
          />

          {/* Display validation errors */}
          {errors.onsiteRemote && (
            <p className="text-red-500 text-sm">
              {errors.onsiteRemote.message}
            </p>
          )}
        </div>
      </div>
      {/* Desired Initial Draft Date */}
      <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
        <h1 className="block text-sm font-medium text-gray-900">
          Desired Initial Draft Date
        </h1>
        <div className="w-[300px] lg:w-[400px] xl:w-[500px]">
          <Controller
            name="draftDate"
            control={control}
            render={({ field }) => (
              <div className="relative w-full">
                <Datepicker
                  {...field}
                  name="draftDate"
                  onChange={field.onChange}
                  placeholder="Select Date"
                  useRange={false}
                  displayFormat="MMMM DD, YYYY"
                  asSingle
                  startFrom={fortyFiveDays}
                  minDate={fortyFiveDays} // Restrict to current or upcoming dates
                  containerClassName="datepicker-dropdown w-[330px] lg:w-[400px] xl:w-[500px]"
                  popperClassName="w-[330px] lg:w-[400px] xl:w-[500px] p-2 border border-gray-300 rounded-lg shadow-lg bg-white"
                  inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                  dayContentRenderer={(date) => {
                    const isDisabled = date < fortyFiveDays;
                    return (
                      <div
                        className={`relative flex items-center justify-center p-1 rounded ${
                          isDisabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "hover:bg-indigo-500 hover:text-white cursor-pointer"
                        }`}
                      >
                        <span>{date.getDate()}</span>
                      </div>
                    );
                  }}
                />
              </div>
            )}
          />

          {errors.draftDate && (
            <p className="text-red-500 text-sm mt-2">
              {errors.draftDate.startDate.message}
            </p>
          )}
        </div>
      </div>
      {/* Additional Details */}
      <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
        <h1 className="block text-sm font-medium text-gray-900">
          Additional Details
        </h1>
        <div>
          <textarea
            {...register("additionalDetails")}
            placeholder="Provide additional request details"
            className="border-gray-300 rounded-md text-sm  w-[330px] lg:w-[400px] xl:w-[500px] "
          />

          {errors.additionalDetails && (
            <p className="text-red-500 text-sm">
              {errors.additionalDetails.message}
            </p>
          )}
        </div>
      </div>
      <div className="mt-10 flex items-center justify-end gap-x-6 border-t py-2 px-5">
        <button
          type="submit"
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          disabled={isPending}
        >
          {isPending ? "Submitting..." : "Submit"}
        </button>
      </div>
      <LocationDrawer
        open={drawerLocationOpen}
        setOpen={setDrawerLocationOpen}
        otherAddress={otherAddress}
        setOtherAddress={setOtherAddress}
      />
      <ProviderContactDrawer
        open={contactOpen}
        setOpen={setContactOpen}
        register={register}
        setFormData={setFormData}
        formData={formData}
        watch={watch}
        onAddContact={handleAddContact} // Pass handler to drawer
      />
      <NotificationModal
        setOpen={setShow}
        open={show}
        title={"Error"}
        desc={message}
        error={true}
      />{" "}
    </div>
  );
};
